import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const sessionUser = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  memberId: z.string().nullable(),
  memberName: z.string().nullable(),
  access: z.object({ projectA: z.boolean(), talent: z.boolean() }),
});

export type SessionUser = z.infer<typeof sessionUser>;

const meResponse = z.object({ user: sessionUser });
const modeResponse = z.object({
  devLogin: z.boolean(),
  google: z.boolean(),
  googleAlt: z.boolean(),
  /** Talent Pool's interim recruiter password form; absent on an api from before it. */
  password: z.boolean().default(false),
});

export type AuthMode = z.infer<typeof modeResponse>;

const SESSION_KEY = ['auth', 'me'];

/** Null means signed out, which is a state rather than a failure. */
async function fetchSession(): Promise<SessionUser | null> {
  const response = await fetch('/auth/me', { credentials: 'same-origin' });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`/auth/me answered ${response.status}`);
  return meResponse.parse(await response.json()).user;
}

export function useSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, retry: false });
}

export function useAuthMode() {
  return useQuery({
    queryKey: ['auth', 'mode'],
    queryFn: async (): Promise<AuthMode> => {
      const response = await fetch('/auth/mode', { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`/auth/mode answered ${response.status}`);
      return modeResponse.parse(await response.json());
    },
  });
}

export function useDevLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { email: string; name: string }) => {
      const response = await fetch('/auth/dev-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(credentials),
      });
      if (!response.ok) throw new Error(await response.text());
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    },
    onSuccess: () => {
      queryClient.clear();
      queryClient.setQueryData(SESSION_KEY, null);
    },
  });
}
